import { notification, TablePaginationConfig, Typography } from 'antd';
import { min } from 'lodash';
import { parse, stringify } from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useLocation, useHistory, useParams } from 'react-router-dom';

import { useSortedClassification } from 'src/hooks/useSortedClassification';
import {
  FinancingNeed,
  getFinancingNeed,
  getFinancingNeeds,
  resetFinancingNeedEvent,
} from 'src/slices/financingNeed';
import {
  DatabaseSearchBar,
  SearchFiltersParameters,
} from '../components/DatabaseSearchBar';
import { DonorCards } from '../components/DonorCards';
import { FullscreenSpin } from '../components/FullscreenSpin';
import { theme } from '../constants/theme';
import { useAppDispatch, useAppSelector } from '../hooks/store';

import {
  Donor,
  getDonorsWithAlgorithm,
  resetDonorEvent,
  resetSuggestions,
  searchDonorsWithAlgorithmAndGetSuggestions,
} from '../slices/donor';
import { getNgo } from '../slices/ngo';
import {
  trackClickMecenePagination,
  trackViewMecenesSelection,
} from '../utils/analytics';
import { checkHasPaidOffer } from '../utils/projects';

const MAX_DISPLAYED_PAGES_NUMBER = 10;

const { Text } = Typography;

const useStyles = createUseStyles({
  container: {
    padding: '89px 61px',
  },
  fondations: {
    fontFamily: 'PP Mori',
    fontSize: '36px',
    fontWeight: '600',
    lineHeight: '100%',
    letterSpacing: '0%',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
  },
  button: {
    fontSize: '0.8200vw',
    margin: 'auto',
    width: '33%',
    textAlign: 'center',
  },
  error: {
    fontSize: '1.500vw',
    paddingTop: '17%',
    paddingLeft: '16.8%',
  },
  filters: {
    display: 'flex',
    flexDirection: 'row',
    marginLeft: '5%',
    marginTop: '1%',
    maxWidth: '100%',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '1.5%',
    '& > *': {
      margin: '5px 0',
    },
  },
  filtersRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  bandeau: {
    padding: '20px',
    width: '1119px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    backgroundColor: '#F3F6FD',
    borderRadius: '20px',
  },
  bandeauTitle: {
    fontFamily: 'PP Mori',
    fontSize: '20px',
    fontWeight: '600',
    lineHeight: '25px',
    verticalAlign: 'middle',
  },
  bandeauSubtitle: {
    fontFamily: 'PP Mori',
    fontWeight: '400',
    fontSize: '16px',
    lineHeight: '19px',
    letterSpacing: '-2%',
    verticalAlign: 'middle',
  },
  bandeauCTA: {
    backgroundColor: theme.palette.primary,
    width: 'fit-content',
    padding: '10px',
    color: 'white',
    borderRadius: '8px',
  },
});

export const DatabaseFromFreeProject: React.VFC = () => {
  const DONORS_MAX_RESULTS = 10;
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const query = useLocation().search;

  const { projectId, offerCompleteExists } = useParams<{
    projectId: string;
    offerCompleteExists: string;
  }>();

  const [searchFilters, setSearchFilters] = useState<SearchFiltersParameters>(
    {},
  );
  const [queryParamsObject, setQueryParamsObject] = useState<
    Record<string, unknown>
  >(parse(query, { arrayFormat: 'bracket' }));

  const {
    donors,
    financingNeeds,
    loading,
    event,
    // addDonorLoading,
    // removeDonorLoading,
    searchResult,
    user,
    offset,
    suggestionsDonors,
    suggestionsLoading,
    classificationsFilter,
    // callForTendersCounts,
  } = useAppSelector(
    ({
      donor: {
        donors,
        loading,
        event,
        suggestionsLoading,
        searchResult,
        suggestionsDonors,
        offset,
        classificationsFilter,
      },
      financingNeed: { financingNeeds, addDonorLoading, removeDonorLoading },
      auth: { user },
      callForTender: { callForTendersCounts },
    }) => ({
      donors,
      classificationsFilter,
      financingNeeds,
      loading,
      event,
      suggestionsLoading,
      addDonorLoading,
      removeDonorLoading,
      searchResult,
      suggestionsDonors,
      user,
      offset,
      callForTendersCounts,
    }),
  );
  const [dropdownFilterVisible, setDropdownFilterVisible] = useState(false);

  const financingNeedEvent = useAppSelector(({ financingNeed: { event } }) => ({
    event,
  })).event;

  const classification = useSortedClassification();
  const fetchDonors = (
    page = 1,
    algorithm = 'tagsProximity',
    searchFilters: SearchFiltersParameters,
  ) => {
    updateQueryParams({ algorithm, page, ...searchFilters });
    dispatch(
      getDonorsWithAlgorithm({
        projectId,
        algorithm: algorithm,
        searchParameters: {
          ...searchFilters,
          offset: (page - 1) * DONORS_MAX_RESULTS,
          limit: DONORS_MAX_RESULTS,
        },
      }),
    );
  };

  const fetchDonorsWithQueryParams = () => {
    const {
      page,
      algorithm,
      name,
      tags,
      statuses,
      targetPersons,
      activityDomains,
      activityZones,
      sustainableDevelopmentGoals,
      targetPopulations,
      donationTypes,
      department,
    } = queryParamsObject;
    const newSearchFilters = {
      name,
      tags,
      statuses,
      targetPersons,
      activityDomains,
      activityZones,
      sustainableDevelopmentGoals,
      targetPopulations,
      donationTypes,
      department,
    } as SearchFiltersParameters;

    setSearchFilters(newSearchFilters);

    fetchDonors(
      page ? parseInt(page as string) : undefined,
      algorithm as string,
      newSearchFilters,
    );
  };

  const updateQueryParams = (params: Record<string, unknown>) => {
    const updatedQueryParamsObject = { ...queryParamsObject, ...params };
    history.replace({
      search: stringify(updatedQueryParamsObject, { arrayFormat: 'bracket' }),
    });
    setQueryParamsObject(updatedQueryParamsObject);
  };

  useEffect(() => {
    // Track page view of mecenes selection screen
    trackViewMecenesSelection();
  }, []);

  useEffect(() => {
    dispatch(getNgo(user?.ngoId));
  }, [dispatch, user?.ngoId]);

  useEffect(() => {
    fetchDonorsWithQueryParams();
    !financingNeeds.allIds.length && dispatch(getFinancingNeeds(user?.ngoId));
  }, [dispatch]);

  useEffect(() => {
    if (event) {
      event.error
        ? notification.error({
            message:
              event.action == 'addFavoriteDonor'
                ? "Le mécène n'a pas pu être ajouté à vos favoris"
                : "Le mécène n'a pas pu être retiré de vos favoris",
            duration: 2,
          })
        : notification.success({
            message:
              event.action == 'addFavoriteDonor'
                ? 'Le mécène a été ajouté à vos favoris'
                : 'Le mécène a été retiré de vos favoris',
            duration: 2,
          });
      dispatch(resetDonorEvent());
    }
  }, [dispatch, event]);

  useEffect(() => {
    if (financingNeedEvent) {
      financingNeedEvent.error
        ? notification.error({
            message:
              financingNeedEvent.action == 'addDonor'
                ? "Le mécène n'a pas pu être ajouté au projet"
                : "Le mécène n'a pas pu être retiré du projet",
            duration: 2,
          })
        : notification.success({
            message:
              financingNeedEvent.action == 'addDonor'
                ? 'Le mécène a été ajouté au projet'
                : 'Le mécène a été retiré du projet',
            duration: 2,
          });
      dispatch(resetFinancingNeedEvent());
    }
  }, [dispatch, financingNeedEvent]);

  const onTableChange = (newPagination: TablePaginationConfig) => {
    const newSearchPage = newPagination.current ?? 0;

    // Redirect to paywall if page > 2 (allow pages 1 and 2 for free)
    if (newSearchPage > 2) {
      history.push(`/subscription/${projectId}/${offerCompleteExists}`);
      return;
    }

    // Track pagination event
    trackClickMecenePagination(newSearchPage);

    fetchDonors(
      newSearchPage,
      queryParamsObject.algorithm as string,
      searchFilters,
    );
  };

  const onSelectAlgorithm = (algorithm: string) => {
    if (!isPremium) {
      history.push(`/subscription/${projectId}/${offerCompleteExists}`);
      return;
    }

    const { page } = queryParamsObject;

    fetchDonors(
      page ? parseInt(page as string) : undefined,
      algorithm,
      searchFilters,
    );
  };

  const onChangeName = (value: string) => {
    value.length
      ? dispatch(
          searchDonorsWithAlgorithmAndGetSuggestions({
            projectId: projectId,
            searchParameters: { ...searchFilters, offset: 0, name: value },
            algorithm:
              (queryParamsObject.algorithm as string) || 'tagsProximity',
          }),
        )
      : dispatch(resetSuggestions());
  };

  if (!user || !classification || !donors) {
    return <FullscreenSpin />;
  }

  useEffect(() => {
    fetchDonors(1, queryParamsObject.algorithm as string, searchFilters);
  }, [dispatch, searchFilters]);

  const displayedFinancingNeed = useMemo<FinancingNeed>(
    () => financingNeeds.byId[projectId],
    [financingNeeds.byId[projectId]],
  );

  useEffect(() => {
    dispatch(getFinancingNeed(projectId));
  }, [dispatch]);

  const isPremium = useMemo<boolean>(
    () => checkHasPaidOffer(displayedFinancingNeed),
    [displayedFinancingNeed],
  );

  return (
    <div
      className={classes.container}
      onClickCapture={(event) => {
        const currentPage = offset / DONORS_MAX_RESULTS + 1;
        // Only allow clicks on donor cards for pages 1 and 2
        if (!isPremium && currentPage > 2) {
          event.stopPropagation();
          // Redirect to paywall
          history.push(`/subscription/${projectId}/${offerCompleteExists}`);
        }
        setDropdownFilterVisible(false);
      }}
    >
      <div className={classes.header}>
        <Text className={classes.fondations}>Mécènes</Text>
        <div
          className={classes.filtersRow}
          onClick={() => {
            if (!isPremium) {
              history.push(`/subscription/${projectId}/${offerCompleteExists}`);
            }
          }}
        >
          <DatabaseSearchBar
            classification={classification}
            onChangeName={(value) => {
              if (!isPremium) {
                history.push(
                  `/subscription/${projectId}/${offerCompleteExists}`,
                );
              } else {
                onChangeName(value);
              }
            }}
            suggestions={suggestionsDonors}
            suggestionsLoading={suggestionsLoading}
            isAutoComplete={false}
            searchParameters={searchFilters}
            setSearchFilters={(filters) => {
              if (!isPremium) {
                history.push(
                  `/subscription/${projectId}/${offerCompleteExists}`,
                );
              } else {
                setSearchFilters(filters);
              }
            }}
            classificationsFilter={classificationsFilter}
            isFromProject={true}
            financingNeed={financingNeeds.byId[projectId]}
            dropdownFilterVisible={dropdownFilterVisible}
            setDropdownFilterVisible={(visible) => {
              if (!isPremium) {
                history.push(
                  `/subscription/${projectId}/${offerCompleteExists}`,
                );
              } else {
                setDropdownFilterVisible(visible);
              }
            }}
            isNgo={true}
            onSelectAlgorithm={onSelectAlgorithm}
          />
        </div>
      </div>
      <DonorCards
        donors={
          searchResult?.resultsIds?.reduce(
            (list: Donor[], donorId) =>
              donors.byId[donorId] ? [...list, donors.byId[donorId]] : list,
            [],
          ) || null
        }
        onTableChange={onTableChange}
        loading={loading}
        financingNeedId={projectId}
        pagination={{
          total: min([
            searchResult?.resultsCount,
            DONORS_MAX_RESULTS * MAX_DISPLAYED_PAGES_NUMBER,
          ]),
          current: offset / DONORS_MAX_RESULTS + 1,
          pageSize: DONORS_MAX_RESULTS,
          showSizeChanger: false,
        }}
        redirectNewPage={false}
        disableClick={!isPremium} // Disable clicks for free users
        onPaywallRedirect={() =>
          history.push(`/subscription/${projectId}/${offerCompleteExists}`)
        }
      />
      <div className={classes.bandeau}>
        <div className={classes.bandeauTitle}>Envie d’aller plus loin ? 🚀</div>
        <div className={classes.bandeauSubtitle}>
          Accédez à tous les mécènes pertinents pour votre projet avec Sonar
          Premium.
        </div>
        <div
          className={classes.bandeauCTA}
          onClick={() =>
            history.push(`/subscription/${projectId}/${offerCompleteExists}`)
          }
          style={{ cursor: 'pointer' }}
        >
          Voir tous les résultats
        </div>
      </div>
    </div>
  );
};
